import Phaser from 'phaser';
import { type EnemyParams, Enemy } from './Enemy';
import { BOSS_CONFIG, BossType, getBossType, BOSS_TYPE_CONFIG } from '../config/enemies';
import type { BossProjectile } from '../config/boss';
import type { VFXSystem } from '../systems/VFXSystem';
import {
  ADD_SPAWN_MIN_RADIUS,
  ADD_SPAWN_RADIUS_VARIANCE,
  CHARGE_SPEED_MULTIPLIER,
  CHARGE_DURATION_MS,
  CAVE_ATTACK_BASE_INTERVAL_MS,
  FOREST_ATTACK_BASE_INTERVAL_MS,
  GRAVEYARD_ATTACK_BASE_INTERVAL_MS,
  PROJECTILE_SPEED,
  PROJECTILE_LIFETIME_MS,
  GRAVEYARD_SPAWN_MIN_RADIUS,
  GRAVEYARD_SPAWN_RADIUS_VARIANCE,
  HUD_BAR_Y,
  HUD_BAR_HEIGHT,
  HUD_BAR_WIDTH,
  GLOW_EXTRA_RADIUS,
  PHASE_2_INTERVAL_MULT,
  PHASE_3_INTERVAL_MULT,
} from '../config/boss';

/**
 * Boss — spawns every 5 waves, each with a unique attack pattern.
 *
 * - Forest Guardian  (wave  5, 25 …): spawns 3 adds then charges the player
 * - Cave Lurker      (wave 10, 30 …): fires projectiles in a spiral pattern
 * - Graveyard Lich   (wave 15, 35 …): continuously summons skeleton adds
 * - Castle Overlord  (wave 20, 40 …): combines all three patterns
 *
 * Visual: 1.5× size, glowing pulsing border, on-screen HUD HP bar.
 * Rewards: 100 gold + 50 XP on defeat (emits 'bossDefeated' on scene events).
 */
export class Boss extends Enemy {
  readonly bossType: BossType;
  phase: 1 | 2 | 3 = 1;

  private readonly glowArc: Phaser.GameObjects.Arc;

  // HUD HP bar elements (fixed to camera)
  private hudBg!: Phaser.GameObjects.Rectangle;
  private hudBar!: Phaser.GameObjects.Rectangle;
  private hudLabel!: Phaser.GameObjects.Text;

  // Attack timers
  private readonly attackTimers: Phaser.Time.TimerEvent[] = [];

  // Cave Boss projectiles updated each frame
  private projectiles: BossProjectile[] = [];

  // Forest / Castle charge state
  private charging = false;
  private chargeSpeedMult = 1;

  // Spiral rotation angle (Cave Boss)
  private spiralAngle = 0;

  /** Callback that inserts an add enemy into the WaveManager's pool. */
  private readonly spawnAdd: (x: number, y: number) => void;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    baseParams: EnemyParams,
    wave: number,
    spawnAdd: (x: number, y: number) => void,
  ) {
    const bossType = getBossType(wave);
    const typeConfig = BOSS_TYPE_CONFIG[bossType];

    const bossParams: EnemyParams = {
      hp:     Math.round(baseParams.hp     * BOSS_CONFIG.hpMultiplier),
      speed:  Math.round(baseParams.speed  * BOSS_CONFIG.speedMultiplier),
      damage: Math.round(baseParams.damage * BOSS_CONFIG.damageMultiplier),
      size:   Math.round(baseParams.size   * BOSS_CONFIG.sizeMultiplier),
      color:  typeConfig.color,
    };

    super(scene, x, y, bossParams);

    this.bossType = bossType;
    this.spawnAdd = spawnAdd;

    // ── Glow ring ──────────────────────────────────────────────────────────
    this.glowArc = scene.add
      .arc(x, y, bossParams.size + GLOW_EXTRA_RADIUS, 0, 360, false, typeConfig.glowColor, 0.6)
      .setDepth(1);

    scene.tweens.add({
      targets: this.glowArc,
      alpha: { from: 0.25, to: 0.9 },
      duration: 500,
      yoyo: true,
      repeat: -1,
    });

    // ── HUD HP bar ─────────────────────────────────────────────────────────
    this.buildHudBar(bossType, typeConfig.name);

    // ── Attack patterns ────────────────────────────────────────────────────
    this.scheduleAttacks();
  }

  // ── Overrides ─────────────────────────────────────────────────────────────

  override takeDamage(amount: number, isCrit = false): boolean {
    // Screen shake on every boss hit (routed through VFXSystem for consistency)
    const vfx = this.scene.registry.get('vfx') as VFXSystem | undefined;
    vfx?.screenShake(6, 150);

    const died = super.takeDamage(amount, isCrit);

    if (this.active) {
      // Phase transitions based on remaining HP
      const hpRatio = this.hp / this.maxHp;
      if (hpRatio <= 0.33 && this.phase < 3) {
        this.phase = 3;
        this.updateAttackFrequency();
      } else if (hpRatio <= 0.66 && this.phase < 2) {
        this.phase = 2;
        this.updateAttackFrequency();
      }
      this.refreshHudBar();
    }

    return died;
  }

  override update(targetX: number, targetY: number, delta: number): void {
    super.update(targetX, targetY, delta);
    this.glowArc.setPosition(this.x, this.y);
    // Cave boss projectiles must be updated every frame
    this.updateProjectiles(delta);
  }

  override destroy(fromScene?: boolean): void {
    // Clean up all attack timers so they stop firing after boss death
    this.attackTimers.forEach(t => t.destroy());
    this.attackTimers.length = 0;
    // Destroy all live projectile arcs
    this.projectiles.forEach(p => p.arc.destroy());
    this.projectiles.length = 0;
    this.glowArc.destroy();
    super.destroy(fromScene);
  }

  /** Called when the boss HP reaches 0. Emits rewards and destroys self. */
  override die(): void {
    this.scene.events.emit('bossDefeated', {
      gold: BOSS_CONFIG.goldReward,
      xp:   BOSS_CONFIG.xpReward,
      type: this.bossType,
    });

    const vfx = this.scene.registry.get('vfx') as VFXSystem | undefined;
    const bossColor = BOSS_TYPE_CONFIG[this.bossType].color;
    if (vfx) {
      // Two overlapping bursts: boss color + gold
      vfx.enemyDeathParticles(this.x, this.y, bossColor, true);
      vfx.enemyDeathParticles(this.x, this.y, 0xffd700, true);
    } else {
      // Fallback
      const particles = this.scene.add.particles(this.x, this.y, undefined, {
        speed:    { min: 80, max: 240 },
        scale:    { start: 0.6, end: 0 },
        lifespan: 600,
        quantity: 30,
        tint:     0xffd700,
        gravityY: 80,
      });
      this.scene.time.delayedCall(700, () => particles.destroy());
    }

    this.destroy();
  }

  // ── HUD helpers ──────────────────────────────────────────────────────────

  private buildHudBar(type: BossType, name: string): void {
    const cam  = this.scene.cameras.main;
    const cx   = cam.width / 2;
    const glowColor = BOSS_TYPE_CONFIG[type].glowColor;

    // Background
    this.hudBg = this.scene.add
      .rectangle(cx, HUD_BAR_Y + HUD_BAR_HEIGHT / 2, HUD_BAR_WIDTH + 6, HUD_BAR_HEIGHT + 6, 0x000000, 0.85)
      .setScrollFactor(0)
      .setDepth(150);

    // Fill bar
    this.hudBar = this.scene.add
      .rectangle(cx - HUD_BAR_WIDTH / 2, HUD_BAR_Y, HUD_BAR_WIDTH, HUD_BAR_HEIGHT, glowColor)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(151);

    // Boss name label
    this.hudLabel = this.scene.add
      .text(cx, HUD_BAR_Y - 2, `⚔ ${name}`, {
        fontSize: '13px',
        fontFamily: 'Arial Black, Arial',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5, 1)
      .setScrollFactor(0)
      .setDepth(152);
  }

  private refreshHudBar(): void {
    if (!this.hudBar.active) return;
    const ratio = Math.max(0, this.hp / this.maxHp);
    const targetWidth = HUD_BAR_WIDTH * ratio;

    // Smooth width transition
    this.scene.tweens.killTweensOf(this.hudBar);
    this.scene.tweens.add({
      targets: this.hudBar,
      width: targetWidth,
      duration: 200,
      ease: 'Linear',
    });
  }

  // ── Attack scheduling ─────────────────────────────────────────────────────

  private scheduleAttacks(): void {
    switch (this.bossType) {
      case 'forest':
        this.scheduleForestAttack();
        break;
      case 'cave':
        this.scheduleCaveAttack();
        break;
      case 'graveyard':
        this.scheduleGraveyardAttack();
        break;
      case 'castle':
        this.scheduleForestAttack();
        this.scheduleCaveAttack();
        this.scheduleGraveyardAttack();
        break;
    }
  }

  private phaseInterval(base: number): number {
    if (this.phase === 3) return base * PHASE_3_INTERVAL_MULT;
    if (this.phase === 2) return base * PHASE_2_INTERVAL_MULT;
    return base;
  }

  /** Re-queue attack timers when phase escalates. */
  private updateAttackFrequency(): void {
    this.attackTimers.forEach(t => t.destroy());
    this.attackTimers.length = 0;
    this.scheduleAttacks();
  }

  // ── Forest attack: spawn 3 adds, then charge ──────────────────────────────

  private scheduleForestAttack(): void {
    const interval = this.phaseInterval(FOREST_ATTACK_BASE_INTERVAL_MS);
    const timer = this.scene.time.addEvent({
      delay: interval,
      callback: this.doForestAttack,
      callbackScope: this,
      loop: true,
    });
    this.attackTimers.push(timer);
  }

  private doForestAttack(): void {
    if (!this.active) return;
    // Spawn 3 adds scattered around the boss
    const addCount = this.phase >= 3 ? 5 : this.phase === 2 ? 4 : 3;
    for (let i = 0; i < addCount; i++) {
      const angle  = (i / addCount) * Math.PI * 2;
      const radius = ADD_SPAWN_MIN_RADIUS + Math.random() * ADD_SPAWN_RADIUS_VARIANCE;
      this.spawnAdd(
        this.x + Math.cos(angle) * radius,
        this.y + Math.sin(angle) * radius,
      );
    }
    // Charge for 1.5 s
    this.charging       = true;
    this.chargeSpeedMult = CHARGE_SPEED_MULTIPLIER;
    this.scene.time.delayedCall(CHARGE_DURATION_MS, () => {
      this.charging = false;
      this.chargeSpeedMult = 1;
    });
  }

  // ── Cave attack: 8-directional spiral projectiles ─────────────────────────

  private scheduleCaveAttack(): void {
    const interval = this.phaseInterval(CAVE_ATTACK_BASE_INTERVAL_MS);
    const timer = this.scene.time.addEvent({
      delay: interval,
      callback: this.doCaveAttack,
      callbackScope: this,
      loop: true,
    });
    this.attackTimers.push(timer);
  }

  private doCaveAttack(): void {
    if (!this.active) return;
    const directions = this.phase >= 2 ? 12 : 8;
    const speed      = PROJECTILE_SPEED;
    const lifetime   = PROJECTILE_LIFETIME_MS;

    for (let i = 0; i < directions; i++) {
      const angle = this.spiralAngle + (i / directions) * Math.PI * 2;
      const vx    = Math.cos(angle) * speed;
      const vy    = Math.sin(angle) * speed;

      const arc = this.scene.add.arc(this.x, this.y, 6, 0, 360, false, 0x9966ff).setDepth(3);
      this.projectiles.push({ arc, vx, vy, lifeMs: lifetime });
    }
    // Rotate spiral each salvo
    this.spiralAngle += Math.PI / 8;
  }

  private updateProjectiles(delta: number): void {
    const dt = delta / 1000;
    const { width, height } = this.scene.cameras.main;
    this.projectiles = this.projectiles.filter(p => {
      p.lifeMs -= delta;
      if (p.lifeMs <= 0 || !p.arc.active) {
        p.arc.destroy();
        return false;
      }
      p.arc.x += p.vx * dt;
      p.arc.y += p.vy * dt;
      // Remove if well off-screen
      if (p.arc.x < -60 || p.arc.x > width + 60 || p.arc.y < -60 || p.arc.y > height + 60) {
        p.arc.destroy();
        return false;
      }
      return true;
    });
  }

  // ── Graveyard attack: summon skeleton adds periodically ───────────────────

  private scheduleGraveyardAttack(): void {
    const interval = this.phaseInterval(GRAVEYARD_ATTACK_BASE_INTERVAL_MS);
    const timer = this.scene.time.addEvent({
      delay: interval,
      callback: this.doGraveyardAttack,
      callbackScope: this,
      loop: true,
    });
    this.attackTimers.push(timer);
  }

  private doGraveyardAttack(): void {
    if (!this.active) return;
    const count = this.phase >= 3 ? 3 : this.phase === 2 ? 2 : 1;
    for (let i = 0; i < count; i++) {
      const angle  = Math.random() * Math.PI * 2;
      const radius = GRAVEYARD_SPAWN_MIN_RADIUS + Math.random() * GRAVEYARD_SPAWN_RADIUS_VARIANCE;
      this.spawnAdd(
        this.x + Math.cos(angle) * radius,
        this.y + Math.sin(angle) * radius,
      );
    }
  }

  /** Returns all active boss projectile arcs (for external collision checks). */
  getProjectiles(): Phaser.GameObjects.Arc[] {
    return this.projectiles.filter(p => p.arc.active).map(p => p.arc);
  }
}

